import BusinessIcon from '@mui/icons-material/Business';
import Groups3Icon from '@mui/icons-material/Groups3';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import RepeatIcon from '@mui/icons-material/Repeat';
import { Box, Card, CardContent, Grid, Link, Stack, Tooltip, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import { useAtom } from 'jotai';
import { sortBy } from 'lodash';
import { allTimezones, useTimezoneSelect } from 'react-timezone-select';

import { getEventFormatColor } from '../../../constants/eventFormatColor';
import type { Event, TagOptionsType } from '../../../Data/types';
import { formModeAtom, selectedEventAtom, showEventPaneAtom } from '../../../state/atoms/atoms';
import OverflowAvatar from '../../shared/OverflowAvatar/OverflowAvatar';
import OverflowChips from '../OverflowChips/OverflowChips';

type Props = {
  event: Event;
};

export default function ListItem({ event }: Readonly<Props>) {
  const { parseTimezone } = useTimezoneSelect({
    labelStyle: 'original',
    timezones: allTimezones,
  });
  const [showEventPane, setshowEventPane] = useAtom(showEventPaneAtom);
  const [, setSelectedEvent] = useAtom(selectedEventAtom);
  const [, setFormMode] = useAtom(formModeAtom);
  const tags: TagOptionsType[] = [
    ...event.practiceGroups.map((pg) => ({ ...pg, color: '#79D92F' })),
    ...event.industries.map((ind) => ({ ...ind, color: '#93D8F7' })),
    ...event.firmInitiatives.map((fi) => ({ ...fi, color: '#FF8B2A' })),
    ...event.departments.map((dep) => ({ ...dep, color: '#DFC5A2' })),
  ];

  const officeLocationTags: TagOptionsType[] = event.officeLocation
    ? event.officeLocation.map((loc) => ({
        id: loc.id,
        name: loc.name,
      }))
    : [];
  const fromattedDayRange = (() => {
    if (event.eventDateTime!.end!.diff(event.eventDateTime!.start, 'days') === 0) {
      return event.eventDateTime!.start!.format('D MMM');
    }
    return event.isRecurring
      ? event.eventDateTime!.start!.format('D MMM')
      : `${event.eventDateTime!.start!.format('D')} - ${event.eventDateTime!.end!.format('D MMM')}`;
  })();
  return (
    <Card>
      <CardContent sx={{ height: '100%' }}>
        <Grid container spacing={1} sx={{ alignItems: 'center' }}>
          <Grid size="auto" sx={{ justifyContent: 'center', alignItems: 'center' }}>
            <Box
              sx={{
                width: '100px',
                backgroundColor: getEventFormatColor(event.eventFormat?.name),
                margin: 'auto',
                minWidth: '100px',
                height: '100px',
                minHeight: '100px',
                borderRadius: '10px',
                justifyContent: 'center',
                alignContent: 'center',
              }}
            >
              <Typography
                sx={{
                  color: grey[900],
                  fontWeight: 'bold',
                  fontSize: 'large',
                  whiteSpace: 'nowrap',
                }}
                align="center"
              >
                {fromattedDayRange}
              </Typography>
              <Typography
                sx={{
                  color: grey[900],
                  fontWeight: 'bold',
                  fontSize: 'medium',
                  whiteSpace: 'nowrap',
                }}
                align="center"
              >
                {event.allDay ? 'All Day' : event.eventDateTime!.start!.format('h:mm A')}
              </Typography>
              {!event.allDay && (
                <Typography
                  sx={{
                    color: grey[900],
                    fontWeight: 'bold',
                    fontSize: 'medium',
                    whiteSpace: 'nowrap',
                  }}
                  align="center"
                >
                  {event.timezone && parseTimezone(event.timezone)?.abbrev}
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid size="grow" sx={{ flexGrow: 1 }}>
            <Grid container rowSpacing={0.25} columnSpacing={1}>
              <Grid size={12}>
                <Link
                  onClick={() => {
                    setFormMode('preview');
                    setshowEventPane(!showEventPane);
                    setSelectedEvent(event);
                  }}
                  component="button"
                  variant="h6"
                  underline="none"
                >
                  {event.title}
                </Link>
              </Grid>
              <Grid size={12}>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'nowrap' }}>
                  {event.hostingType === 'internal' && (
                    <Tooltip title="Internal" arrow placement="top">
                      <BusinessIcon fontSize="small" />
                    </Tooltip>
                  )}
                  {event.hostingType === 'external' && (
                    <Tooltip title="External" arrow placement="top">
                      <Groups3Icon fontSize="small" />
                    </Tooltip>
                  )}
                  {event.hostingType === '3rd party' && (
                    <Tooltip title="3rd Party" arrow placement="top">
                      <Groups3Icon fontSize="small" />
                    </Tooltip>
                  )}
                  {event.inviteOnly && (
                    <Tooltip title="Invite Only" arrow placement="top">
                      <LockPersonIcon fontSize="small" />
                    </Tooltip>
                  )}
                  {event.isRecurring && (
                    <Tooltip title="Recurring" arrow placement="top">
                      <RepeatIcon fontSize="small" />
                    </Tooltip>
                  )}
                </Stack>
              </Grid>
              {!event.isLocationExternal && officeLocationTags && officeLocationTags.length > 0 && (
                <Grid size={12}>
                  <Grid container spacing={1} sx={{ alignItems: 'center' }}>
                    <Grid size="auto">
                      <Typography variant="body2" noWrap sx={{ fontWeight: 'bold' }}>
                        Location(s):
                      </Typography>
                    </Grid>
                    <Grid size="grow">
                      <OverflowChips items={sortBy(officeLocationTags, 'name')} />
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {event.isLocationExternal && event.externalLocation && (
                <Grid size={12}>
                  <Grid container spacing={1} sx={{ alignItems: 'center' }}>
                    <Grid size="auto">
                      <Typography variant="body2" noWrap sx={{ fontWeight: 'bold' }}>
                        Location(s):
                      </Typography>
                    </Grid>
                    <Grid size="grow">
                      <OverflowChips items={[{ id: event.externalLocation, name: event.externalLocation }]} />
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {tags && tags.length > 0 && (
                <Grid size={12}>
                  <OverflowChips items={sortBy(tags, 'name')} />
                </Grid>
              )}
              {event.audience && event.audience.length > 0 && (
                <Grid size={{ md: event.eventType ? 6 : 12, xs: 12 }}>
                  <Grid container spacing={1} sx={{ alignItems: 'center' }}>
                    <Grid size="auto">
                      <Typography variant="body2" noWrap sx={{ fontWeight: 'bold' }}>
                        Audience:
                      </Typography>
                    </Grid>
                    <Grid size="grow">
                      <Typography variant="body2" noWrap>
                        {event.audience.map((a) => a.name).join(', ')}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {event.eventType && (
                <Grid
                  size={{
                    md: event.audience && event.audience.length > 0 ? 6 : 12,
                    xs: 12,
                  }}
                >
                  <Grid container spacing={1} sx={{ alignItems: 'center' }}>
                    <Grid size="auto">
                      <Typography variant="body2" noWrap sx={{ fontWeight: 'bold' }}>
                        Event Type:
                      </Typography>
                    </Grid>
                    <Grid size="grow">
                      <Typography variant="body2" noWrap>
                        {event.eventType.event_type}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {((event.mlSpeaker && event.mlSpeaker.length > 0) ||
                (event.outsideSpeakers && event.outsideSpeakers.length > 0)) && (
                <Grid
                  size={{
                    md: event.eventContact && event.eventContact.length > 0 ? 6 : 12,
                    xs: 12,
                  }}
                >
                  <Grid container spacing={1} sx={{ alignItems: 'center' }}>
                    <Grid size="auto">
                      <Typography variant="body2" noWrap sx={{ fontWeight: 'bold' }}>
                        Speaker:
                      </Typography>
                    </Grid>
                    <Grid size="grow">
                      {event.mlSpeaker && event.mlSpeaker.length > 0 && <OverflowAvatar items={event.mlSpeaker} />}
                      {event.outsideSpeakers && event.outsideSpeakers.length > 0 && (
                        <OverflowAvatar
                          items={event.outsideSpeakers?.map((os) => ({
                            DisplayName: os.name,
                            DisplayTitle: os.title,
                            PracticeGrpName: os.organization,
                          }))}
                        />
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {event.eventContact && event.eventContact.length > 0 && (
                <Grid
                  size={{
                    md:
                      (event.mlSpeaker && event.mlSpeaker.length > 0) ||
                      (event.outsideSpeakers && event.outsideSpeakers.length > 0)
                        ? 6
                        : 12,
                    xs: 12,
                  }}
                >
                  <Grid container spacing={1} sx={{ alignItems: 'center' }}>
                    <Grid size="auto">
                      <Typography variant="body2" noWrap sx={{ fontWeight: 'bold' }}>
                        Event Contact:
                      </Typography>
                    </Grid>
                    <Grid size="grow">
                      <OverflowAvatar items={event.eventContact} />
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {event.sponsoringAttorney && event.sponsoringAttorney.length > 0 && (
                <Grid size={12}>
                  <Grid container spacing={1} sx={{ alignItems: 'center' }}>
                    <Grid size="auto">
                      <Typography variant="body2" noWrap sx={{ fontWeight: 'bold' }}>
                        Sponsoring Attorney:
                      </Typography>
                    </Grid>
                    <Grid size="grow">
                      <OverflowAvatar items={event.sponsoringAttorney} />
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
